<template lang="pug">
base-section#article(space='48')
  base-page-content
    .article-layout
      article.article-main
        .article-meta
          span.article-date {{ formattedDate }}
        h1.article-title {{ article.title }}
        figure.article-figure
          .article-figure-frame(aria-hidden='true')
          NuxtPicture.article-image(
            :src='articleImage'
            :img-attrs='{ class: "article-image-media" }'
            sizes='xs:100vw sm:100vw md:70vw lg:900px'
            fit='cover'
          )
        .article-body
          content-renderer.content-renderer(:value='article')
      aside.article-aside
        section.meta-panel(v-if='article.authors?.length')
          h2.meta-title {{ t('pages.articles.authors') }}
          .author-stack
            component.author-card(
              v-for='author in article.authors'
              :key='author.name'
              :is='authorCardTag(author.link)'
              :class='{ "author-card--static": !normalizedAuthorLink(author.link) }'
              :href='normalizedAuthorLink(author.link) || undefined'
              :rel='authorCardRel(author.link)'
              :target='authorCardTarget(author.link)'
            )
              v-avatar.author-avatar(size='52')
                NuxtPicture(
                  v-if='author.src'
                  :src='author.src'
                  width='52'
                  height='52'
                  fit='cover'
                  sizes='52px'
                  :img-attrs='{ class: "author-avatar-media" }'
                )
                base-image-placeholder.author-avatar-placeholder(v-else)
              .author-copy
                strong {{ author.name }}
                span(v-if='author.blurb') {{ author.blurb }}
        section.meta-panel(v-if='article.related_works?.length')
          h2.meta-title {{ t('pages.articles.relatedWorks') }}
          ul.related-list
            li(v-for='work in article.related_works', :key='work.name')
              a(:href='work.link', target='_blank', rel='noopener') {{ work.name }}
</template>

<script setup lang="ts">
import { useNuxtApp } from 'nuxt/app'
import { publicAssetPath } from '~/utils/assets'
import { toDayjsLocale } from '~/utils/i18n'

defineOptions({
  name: 'SectionNews',
})

const props = defineProps({
  article: { type: Object, default: () => null },
})

const { $dayjs } = useNuxtApp()
const { locale, t } = useI18n()

const formattedDate = computed(() =>
  $dayjs(props.article.date).locale(toDayjsLocale(locale.value)).format('LL'),
)
const articleImage = computed(() => publicAssetPath(props.article.image))

function normalizedAuthorLink(link?: string | null) {
  const normalizedLink = link?.trim()

  if (!normalizedLink || normalizedLink === '#') {
    return null
  }

  return normalizedLink
}

function authorCardTag(link?: string | null) {
  return normalizedAuthorLink(link) ? 'a' : 'div'
}

function authorCardTarget(link?: string | null) {
  const normalizedLink = normalizedAuthorLink(link)
  return normalizedLink?.startsWith('http') ? '_blank' : undefined
}

function authorCardRel(link?: string | null) {
  return authorCardTarget(link) ? 'noopener noreferrer' : undefined
}
</script>

<style scoped>
#article {
  background: var(--surface-page);
}

.article-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(16rem, 0.78fr);
  gap: 2.25rem;
}

.article-main {
  min-width: 0;
}

.article-meta {
  margin-bottom: 0.75rem;
}

.article-date {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.7rem;
  border: 1px solid var(--border-default);
  font-size: 0.75rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.article-title {
  margin: 0;
  font-size: clamp(2rem, 3.4vw, 3.4rem);
  line-height: 0.94;
  letter-spacing: -0.04em;
}

.article-figure {
  margin: 1.4rem 0 2rem;
  border: 1px solid var(--border-default);
  background: var(--surface-card);
  padding: 0.8rem;
}

.article-image {
  display: block;
  border: 1px solid var(--border-default);
  background: var(--surface-muted);
}

:deep(.article-image picture) {
  display: block;
}

:deep(.article-image-media) {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
}

.article-body {
  color: var(--color-text-strong);
  font-family: var(--font-serif);
  font-size: 1rem;
  line-height: 1.8;
  text-align: justify;
  hyphens: auto;
  hanging-punctuation: first last;
}

:deep(.article-body p) {
  margin: 0 0 1.35rem;
}

:deep(.article-body h2),
:deep(.article-body h3),
:deep(.article-body h4) {
  margin: 2.1rem 0 0.9rem;
  font-family: inherit;
  line-height: 1.2;
}

:deep(.article-body a) {
  color: var(--color-text-strong);
  text-decoration-thickness: 1px;
  text-underline-offset: 0.18em;
}

:deep(.article-body ul),
:deep(.article-body ol) {
  margin: 0 0 1.35rem 1.5rem;
}

.article-aside {
  display: grid;
  align-content: start;
  gap: 1.25rem;
}

.meta-panel {
  padding: 1.2rem;
  border: 1px solid var(--border-default);
  background: var(--surface-card);
}

.meta-title {
  position: relative;
  margin: 0 0 0.9rem;
  font-size: 0.82rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.author-stack {
  display: grid;
  gap: 0.85rem;
}

.author-card {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  gap: 0.8rem;
  align-items: start;
  color: inherit;
  text-decoration: none;
}

.author-card--static {
  cursor: default;
}

.author-avatar {
  border: 1px solid var(--border-default);
  overflow: hidden;
}

:deep(.author-avatar-media) {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.author-avatar-placeholder {
  --image-placeholder-padding: 0.4rem;
  --image-placeholder-background: var(--surface-page);
  --image-placeholder-logo-width: 76%;
  --image-placeholder-logo-max-width: 1.75rem;
  --image-placeholder-logo-opacity: 0.28;
  --image-placeholder-logo-blur: 0.8px;
}

.author-copy {
  display: grid;
  gap: 0.3rem;
}

.author-copy strong {
  line-height: 1.2;
}

.author-copy span {
  font-size: 0.92rem;
  color: var(--color-text-muted);
  line-height: 1.45;
}

.related-list {
  position: relative;
  margin: 0;
  padding-left: 1.1rem;
}

.related-list li + li {
  margin-top: 0.6rem;
}

.related-list a {
  color: var(--color-text-strong);
}

@media (max-width: 960px) {
  .article-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .article-title {
    font-size: 2rem;
  }

  .article-body {
    font-size: 0.97rem;
    line-height: 1.75;
  }
}
</style>
